import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LocuLogo from './LocuLogo';
import axios from 'axios';
import {
  MapPinIcon,
  ChevronDownIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  HomeIcon,
  BuildingStorefrontIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Navbar = () => {
  const { isAuthenticated, user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userType, setUserType] = useState('members');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [storedUserType, setStoredUserType] = useState(() => {
    return typeof window !== 'undefined' ? localStorage.getItem('locuUserType') : null;
  });

  // Check if user is a merchant - explicitly check for restaurant role
  const isMerchant = isAuthenticated && user && (user.role === 'merchant' || user.role === 'merchant');
  
  // Listen for user type changes (when switching between members/merchants on home page)
  useEffect(() => {
    const handleUserTypeChange = () => {
      const newUserType = localStorage.getItem('locuUserType');
      setStoredUserType(newUserType);
    };
    
    window.addEventListener('userTypeChanged', handleUserTypeChange);
    return () => window.removeEventListener('userTypeChanged', handleUserTypeChange);
  }, []);
  
  // Check if we're on a merchant-related page (even if not authenticated)
  // Also check localStorage for merchant view on home page
  const isMerchantView = storedUserType === 'merchants';
  
  // Check if merchant intent is stored (for login/signup/otp pages)
  const merchantIntent = typeof window !== 'undefined' ? 
    (localStorage.getItem('merchantIntent') === 'true' || 
     sessionStorage.getItem('isMerchant') === 'true') : false;
  
  // Check if we're on auth pages with merchant intent
  const isAuthPageWithMerchantIntent = (location.pathname === '/login' || 
                                        location.pathname === '/signup' || 
                                        location.pathname === '/otp' ||
                                        location.pathname === '/register') && merchantIntent;
  
  const isMerchantPage = location.pathname.startsWith('/merchant') || 
                         location.pathname.startsWith('/register-restaurant') ||
                         location.pathname.startsWith('/add-business') ||
                         location.pathname === '/merchant-contact' ||
                         location.pathname === '/restaurant/register' ||
                         (location.pathname === '/' && isMerchantView) ||
                         isAuthPageWithMerchantIntent;

  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by your browser');
      return;
    }

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Try to reverse geocode using a free service (you can replace with your own API)
          // Using OpenStreetMap Nominatim (free, no API key needed)
          const response = await axios.get(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
            {
              headers: {
                'User-Agent': 'LOCU App'
              }
            }
          );

          const data = response.data;
          if (data) {
            const address = data.address || {};
            // Build a clean address string
            const addressParts = [
              address.suburb || address.neighbourhood,
              address.city || address.town || address.village,
              address.state,
              address.postcode
            ].filter(Boolean);

            const locationAddress = addressParts.join(', ') || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;

            const locationData = {
              name: 'Current Location',
              address: locationAddress,
              latitude,
              longitude
            };

            setCurrentLocation(locationData);

            // Save to localStorage for non-authenticated users or as backup
            localStorage.setItem('selectedLocation', JSON.stringify({
              address: locationAddress,
              latitude,
              longitude,
              timestamp: Date.now()
            }));

            // Update user location in backend if authenticated
            if (isAuthenticated) {
              try {
                await axios.post(`${API_URL}/neighborhood/location`, {
                  latitude,
                  longitude,
                  enabled: true,
                  address: locationAddress
                });
                await refreshUser();
              } catch (error) {
                console.error('Error updating location:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error reverse geocoding:', error);
          // Fallback to coordinates
          const locationAddress = `Lat: ${latitude.toFixed(4)}, Lng: ${longitude.toFixed(4)}`;
          setCurrentLocation({
            name: 'Current Location',
            address: locationAddress,
            latitude,
            longitude
          });
          
          // Save to localStorage even with coordinates
          localStorage.setItem('selectedLocation', JSON.stringify({
            address: locationAddress,
            latitude,
            longitude,
            timestamp: Date.now()
          }));
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        console.error('Location access denied:', error);
        setLocationLoading(false);
        // Don't show error to user - they can manually select location
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000 // Accept location up to 1 minute old
      }
    );
  }, [isAuthenticated, refreshUser]);

  // Get live location on mount - automatically request current location (skip on merchant pages)
  useEffect(() => {
    // Don't fetch location on merchant pages
    if (isMerchantPage) {
      return;
    }

    // Always try to get current location when app loads
    if (navigator.geolocation) {
      getCurrentLocation();
    }

    // Set up periodic location updates (every 5 minutes) like District.in
    const locationUpdateInterval = setInterval(() => {
      if (navigator.geolocation && !isMerchantPage) {
        getCurrentLocation();
      }
    }, 5 * 60 * 1000); // Update every 5 minutes

    return () => clearInterval(locationUpdateInterval);
  }, [getCurrentLocation, isMerchantPage]); // Include getCurrentLocation and isMerchantPage since it's stable (useCallback)

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleUserTypeChange = (type) => {
    setUserType(type);
    setDropdownOpen(false);
    localStorage.setItem('locuUserType', type);

    // Clear merchant intent flags when switching to members view
    if (type === 'members') {
      localStorage.removeItem('merchantIntent');
      sessionStorage.removeItem('isMerchant');
    }

    if (location.pathname === '/') {
      window.dispatchEvent(new Event('userTypeChanged'));
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const savedUserType = localStorage.getItem('locuUserType');
    if (savedUserType) {
      setUserType(savedUserType);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.user-type-dropdown')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const isActive = (path) => {
    // Handle both actual pages and feature pages
    if (path === '/dashboard' || path === '/feature/home') {
      return location.pathname === '/dashboard' || location.pathname === '/feature/home';
    }
    if (path === '/pay-rent' || path === '/feature/pay-rent') {
      return location.pathname === '/pay-rent' || location.pathname === '/feature/pay-rent';
    }
    if (path === '/neighborhood' || path === '/feature/neighborhood') {
      return location.pathname === '/neighborhood' || location.pathname === '/feature/neighborhood';
    }
    if (path === '/dining-events' || path === '/feature/hola') {
      return location.pathname.startsWith('/dining-events') || location.pathname === '/feature/hola';
    }
    if (path === '/profile') {
      return location.pathname === '/profile';
    }
    return false;
  };

  const getUserInitial = () => {
    if (user?.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    if (user?.name) {
      return user.name.split(' ')[0].charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Get location display - prioritize live location, then property address, then localStorage
  const getLocationDisplay = () => {
    if (currentLocation && currentLocation.address) {
      return {
        address: currentLocation.address
      };
    }
    
    if (user?.propertyAddress) {
      return {
        address: user.propertyAddress
      };
    }

    // Check localStorage for non-authenticated users
    const savedLocation = localStorage.getItem('selectedLocation');
    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation);
        // Check if location is less than 7 days old
        if (Date.now() - location.timestamp < 7 * 24 * 60 * 60 * 1000) {
          return {
            address: location.address
          };
        }
      } catch (error) {
        console.error('Error parsing saved location:', error);
      }
    }
    
    return {
      address: 'Select Location'
    };
  };

  const locationDisplay = getLocationDisplay();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 relative">
          {/* Left Side - Logo, App Name, and Location */}
          <div className="flex items-center flex-shrink-0 min-w-0 max-w-[50%]">
            {/* Logo Icon Only */}
            <Link to="/" className="flex items-center hover:opacity-90 transition-opacity flex-shrink-0">
              <LocuLogo showText={false} textSize="text-xl md:text-2xl" textColor="text-gray-900" />
            </Link>

            {/* App Name "LOCU" - Always visible */}
            <Link to="/" className="flex items-center hover:opacity-90 transition-opacity flex-shrink-0 ml-2 md:ml-3">
              <span className="font-bold text-lg md:text-2xl text-gray-900 tracking-tight whitespace-nowrap">LOCU</span>
            </Link>

            {/* Separator - Divider line after app name - Hidden on small screens */}
            <div className="hidden sm:block h-8 w-px bg-gray-300 ml-3 md:ml-6 mr-3 md:mr-6 flex-shrink-0"></div>

            {/* Location - Responsive with truncation - Hidden on merchant pages and merchant intent */}
            {!isMerchantPage && !isMerchantView && !merchantIntent && (
              <>
                <div className="hidden sm:flex items-center gap-1 md:gap-2 flex-shrink-0 min-w-0">
                  <div 
                    className="flex items-center gap-1 md:gap-2 cursor-pointer hover:opacity-80 transition-opacity min-w-0" 
                    onClick={() => navigate('/select-location')}
                  >
                    <MapPinIcon className={`h-4 w-4 md:h-5 md:w-5 flex-shrink-0 ${locationDisplay.address !== 'Select Location' ? 'text-purple-600' : 'text-gray-400'}`} />
                    <div className="flex flex-col flex-shrink-0 min-w-0">
                      <span className={`text-xs md:text-sm leading-tight truncate max-w-[100px] lg:max-w-[150px] xl:max-w-none ${locationDisplay.address !== 'Select Location' ? 'text-gray-900 font-semibold' : 'text-gray-400'}`}>
                        {locationDisplay.address}
                      </span>
                    </div>
                    {locationLoading ? (
                      <div className="ml-1 animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-b-2 border-purple-600 flex-shrink-0"></div>
                    ) : (
                      <ChevronDownIcon className={`h-3 w-3 md:h-4 md:w-4 flex-shrink-0 ml-1 ${locationDisplay.address !== 'Select Location' ? 'text-purple-400' : 'text-gray-400'}`} />
                    )}
                  </div>
                  {/* Refresh location button - Hidden on small screens */}
                  {!locationLoading && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        getCurrentLocation();
                      }}
                      className="hidden md:block p-1 hover:bg-gray-100 rounded-full transition-colors"
                      title="Refresh location"
                    >
                      <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Mobile Location Button - Only on small screens */}
                <div className="sm:hidden flex items-center ml-2">
                  <button
                    onClick={() => navigate('/select-location')}
                    className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                    title={locationDisplay.address}
                  >
                    <MapPinIcon className={`h-5 w-5 ${locationDisplay.address !== 'Select Location' ? 'text-purple-600' : 'text-gray-400'}`} />
                  </button>
                </div>
              </>
            )}

            {/* User Type Dropdown - Only show when not authenticated, hidden on mobile to prevent overlap */}
            {!isAuthenticated && (
              <div className="hidden lg:block relative user-type-dropdown ml-2 lg:ml-4">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center space-x-1 px-3 lg:px-4 py-2 text-sm lg:text-base text-gray-700 hover:text-gray-900 font-semibold transition"
                >
                  <span className="whitespace-nowrap">For {userType === 'members' ? 'members' : 'merchants'}</span>
                  <ChevronDownIcon className={`h-4 w-4 lg:h-5 lg:w-5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl z-50 border border-gray-200">
                    <div className="py-2">
                      <button
                        onClick={() => handleUserTypeChange('members')}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <UserGroupIcon className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-gray-900 font-semibold">LOCU Rewards</p>
                            <p className="text-xs text-gray-500">For members</p>
                          </div>
                        </div>
                        {userType === 'members' && (
                          <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded">Active</span>
                        )}
                      </button>

                      <button
                        onClick={() => handleUserTypeChange('merchants')}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <BuildingOffice2Icon className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-gray-900 font-semibold">Neighborhood Rewards</p>
                            <p className="text-xs text-gray-500">For merchants</p>
                          </div>
                        </div>
                        {userType === 'merchants' && (
                          <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded">Active</span>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Center - Navigation - Horizontal Links with Icons (Only for Members, NOT for Merchants) */}
          {/* Hide these links for merchants AND on merchant-related pages */}
          {!isMerchant && !isMerchantPage && !isMerchantView && !merchantIntent && (
            <div className="hidden lg:flex items-center space-x-0.5 absolute left-1/2 -translate-x-1/2 z-10">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className={`flex items-center gap-1.5 px-2 lg:px-3 py-2 rounded-full text-sm font-semibold transition ${
                      isActive('/dashboard')
                        ? 'bg-pink-100 text-pink-700'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Home"
                  >
                    <HomeIcon className={`h-4 w-4 ${isActive('/dashboard') ? 'text-pink-700' : 'text-gray-700'}`} />
                    <span className="hidden xl:inline">Home</span>
                  </Link>

                  <Link
                    to="/pay-rent"
                    className={`flex items-center gap-1.5 px-2 lg:px-3 py-2 rounded-full text-sm font-semibold transition ${
                      isActive('/pay-rent')
                        ? 'bg-pink-100 text-pink-700'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Pay Rent"
                  >
                    <CurrencyDollarIcon className={`h-4 w-4 ${isActive('/pay-rent') ? 'text-pink-700' : 'text-gray-700'}`} />
                    <span className="hidden xl:inline">Pay Rent</span>
                  </Link>

                  <Link
                    to="/neighborhood"
                    className={`flex items-center gap-1.5 px-2 lg:px-3 py-2 rounded-full text-sm font-semibold transition ${
                      isActive('/neighborhood')
                        ? 'bg-pink-100 text-pink-700'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Neighborhood"
                  >
                    <BuildingStorefrontIcon className={`h-4 w-4 ${isActive('/neighborhood') ? 'text-pink-700' : 'text-gray-700'}`} />
                    <span className="hidden xl:inline">Neighborhood</span>
                  </Link>

                  <Link
                    to="/dining-events"
                    className={`flex items-center gap-1.5 px-2 lg:px-3 py-2 rounded-full text-sm font-semibold transition ${
                      isActive('/dining-events')
                        ? 'bg-pink-100 text-pink-700'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Connect"
                  >
                    <UserGroupIcon className={`h-4 w-4 ${isActive('/dining-events') ? 'text-pink-700' : 'text-gray-700'}`} />
                    <span className="hidden xl:inline">Connect</span>
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/feature/home"
                    className={`flex items-center gap-1.5 px-2 lg:px-3 py-2 rounded-full text-sm font-semibold transition ${
                      isActive('/feature/home')
                        ? 'bg-pink-100 text-pink-700'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Home"
                  >
                    <HomeIcon className={`h-4 w-4 ${isActive('/feature/home') ? 'text-pink-700' : 'text-gray-700'}`} />
                    <span className="hidden xl:inline">Home</span>
                  </Link>

                  <Link
                    to="/feature/pay-rent"
                    className={`flex items-center gap-1.5 px-2 lg:px-3 py-2 rounded-full text-sm font-semibold transition ${
                      isActive('/feature/pay-rent')
                        ? 'bg-pink-100 text-pink-700'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Pay Rent"
                  >
                    <CurrencyDollarIcon className={`h-4 w-4 ${isActive('/feature/pay-rent') ? 'text-pink-700' : 'text-gray-700'}`} />
                    <span className="hidden xl:inline">Pay Rent</span>
                  </Link>

                  <Link
                    to="/feature/neighborhood"
                    className={`flex items-center gap-1.5 px-2 lg:px-3 py-2 rounded-full text-sm font-semibold transition ${
                      isActive('/feature/neighborhood')
                        ? 'bg-pink-100 text-pink-700'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Neighborhood"
                  >
                    <BuildingStorefrontIcon className={`h-4 w-4 ${isActive('/feature/neighborhood') ? 'text-pink-700' : 'text-gray-700'}`} />
                    <span className="hidden xl:inline">Neighborhood</span>
                  </Link>

                  <Link
                    to="/feature/hola"
                    className={`flex items-center gap-1.5 px-2 lg:px-3 py-2 rounded-full text-sm font-semibold transition ${
                      isActive('/feature/hola')
                        ? 'bg-pink-100 text-pink-700'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                    title="Connect"
                  >
                    <UserGroupIcon className={`h-4 w-4 ${isActive('/feature/hola') ? 'text-pink-700' : 'text-gray-700'}`} />
                    <span className="hidden xl:inline">Connect</span>
                  </Link>
                </>
              )}
            </div>
          )}

          {/* Right Side - Feedback + Profile Icon (Rightmost) */}
          <div className="flex items-center flex-shrink-0 ml-auto">
            {/* Desktop Feedback Link */}
            <Link
              to="/feedback"
              className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-gray-700 hover:text-gray-900 hover:bg-gray-50 transition mr-2"
              title="Feedback"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" />
              <span>Feedback</span>
            </Link>
            {isAuthenticated ? (
              <>
                <Link
                  to="/profile"
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-black text-white flex items-center justify-center font-bold text-sm md:text-base hover:opacity-90 transition-opacity overflow-hidden"
                >
                  {user?.photo ? (
                    <img src={user.photo} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    getUserInitial()
                  )}
                </Link>
              </>
            ) : (
              <>
                {location.pathname !== '/merchant-contact' && (
                  <button
                    onClick={() => navigate('/login', { state: { isMerchant: isMerchantView || merchantIntent } })}
                    className="hidden sm:block text-gray-900 hover:text-gray-700 px-3 md:px-5 py-2 md:py-2.5 text-sm md:text-base font-semibold whitespace-nowrap cursor-pointer"
                  >
                    Sign in / Sign up
                  </button>
                )}
              </>
            )}

            {/* Mobile Menu Button - Show on screens smaller than lg (when center nav is hidden) */}
            <div className="lg:hidden ml-2 md:ml-4">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                aria-label="Toggle menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {menuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="xl:hidden bg-white border-t border-gray-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
          {/* Location Section - Hidden on merchant pages and merchant intent */}
          {!isMerchantPage && !isMerchantView && !merchantIntent && (
            <div className="px-4 py-3 border-b border-gray-200">
              <button
                onClick={() => {
                  navigate('/select-location');
                  setMenuOpen(false);
                }}
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-3">
                  <MapPinIcon className={`h-5 w-5 flex-shrink-0 ${locationDisplay.address !== 'Select Location' ? 'text-purple-600' : 'text-gray-400'}`} />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-xs text-gray-500">Location</span>
                    <span className={`text-sm font-semibold truncate ${locationDisplay.address !== 'Select Location' ? 'text-gray-900' : 'text-gray-400'}`}>
                      {locationDisplay.address}
                    </span>
                  </div>
                </div>
                <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              </button>
            </div>
          )}

          {/* User Type Dropdown - Only for non-authenticated users */}
          {!isAuthenticated && (
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="text-xs text-gray-500 mb-2">View as</div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    handleUserTypeChange('members');
                    setMenuOpen(false);
                  }}
                  className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition ${
                    userType === 'members'
                      ? 'border-purple-600 bg-purple-50 text-purple-700'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <UserGroupIcon className="h-4 w-4" />
                  <span className="text-sm font-semibold">Members</span>
                </button>
                <button
                  onClick={() => {
                    handleUserTypeChange('merchants');
                    setMenuOpen(false);
                  }}
                  className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition ${
                    userType === 'merchants'
                      ? 'border-purple-600 bg-purple-50 text-purple-700'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <BuildingOffice2Icon className="h-4 w-4" />
                  <span className="text-sm font-semibold">Merchants</span>
                </button>
              </div>
            </div>
          )}

          {/* Navigation Links - Only for Members, NOT for Merchants */}
          {!isMerchant && !isMerchantPage && (
            <div className="px-2 pt-2 pb-3 space-y-1">
              {/* Hide Home, Pay Rent, Neighborhood, Hola for merchants and merchant pages */}
              {!isMerchant && !isMerchantPage && !isMerchantView && !merchantIntent && (
                <>
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-3 px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                        onClick={() => setMenuOpen(false)}
                      >
                        <HomeIcon className="h-5 w-5" />
                        <span>Home</span>
                      </Link>
                      <Link
                        to="/pay-rent"
                        className="flex items-center gap-3 px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                        onClick={() => setMenuOpen(false)}
                      >
                        <CurrencyDollarIcon className="h-5 w-5" />
                        <span>Pay Rent</span>
                      </Link>
                      <Link
                        to="/neighborhood"
                        className="flex items-center gap-3 px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                        onClick={() => setMenuOpen(false)}
                      >
                        <BuildingStorefrontIcon className="h-5 w-5" />
                        <span>Neighborhood</span>
                      </Link>
                      <Link
                        to="/dining-events"
                        className="flex items-center gap-3 px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                        onClick={() => setMenuOpen(false)}
                      >
                        <UserGroupIcon className="h-5 w-5" />
                        <span>Connect</span>
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/feature/home"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                      >
                        <HomeIcon className="h-5 w-5" />
                        <span>Home</span>
                      </Link>
                      <Link
                        to="/feature/pay-rent"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                      >
                        <CurrencyDollarIcon className="h-5 w-5" />
                        <span>Pay Rent</span>
                      </Link>
                      <Link
                        to="/feature/neighborhood"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                      >
                        <BuildingStorefrontIcon className="h-5 w-5" />
                        <span>Neighborhood</span>
                      </Link>
                      <Link
                        to="/feature/hola"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                      >
                        <UserGroupIcon className="h-5 w-5" />
                        <span>Connect</span>
                      </Link>
                    </>
                  )}
                </>
              )}
              {isAuthenticated && (
                <Link
                  to="/profile"
                  className="flex items-center gap-3 px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                  onClick={() => setMenuOpen(false)}
                >
                  <span>Profile</span>
                </Link>
              )}
              <Link
                to="/feedback"
                className="flex items-center gap-3 px-3 py-2.5 text-base font-semibold text-gray-700 hover:bg-gray-100 rounded-md"
                onClick={() => setMenuOpen(false)}
              >
                <ChatBubbleLeftRightIcon className="h-5 w-5" />
                <span>Feedback</span>
              </Link>
            </div>
          )}

          {/* User Info & Actions */}
          {isAuthenticated ? (
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="px-4 mb-3">
                <div className="text-base font-semibold text-gray-900 truncate">{user?.name}</div>
                <div className="text-sm font-medium text-gray-500 truncate">{user?.email}</div>
              </div>
              <button
                onClick={() => {
                  handleLogout();
                  setMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2.5 text-base font-semibold text-red-600 hover:text-red-700 hover:bg-gray-100 rounded-md"
              >
                Logout
              </button>
            </div>
          ) : (
            location.pathname !== '/merchant-contact' && (
              <div className="pt-3 pb-3 border-t border-gray-200 px-4">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/login', { state: { isMerchant: isMerchantView || merchantIntent } });
                  }}
                  className="block w-full text-center px-4 py-2.5 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition"
                >
                  Sign in / Sign up
                </button>
              </div>
            )
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
